import React, { useEffect, useState } from "react";
import {
  UseFormRegister,
  FieldError,
  FieldErrorsImpl,
  Merge,
} from "react-hook-form";
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isEqual,
  startOfMonth,
  startOfDay,
  getDay,
} from "date-fns";

interface DateRangePickerProps {
  label?: string;
  name: string;
  register?: UseFormRegister<any>;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
  onChange?: (value: { startDate: Date | null; endDate: Date | null }) => void;
  // The selected range, for restoring a saved selection (e.g. reopening a
  // filter modal). Omit it to leave the picker uncontrolled; null clears it.
  value?: { startDate: Date | null; endDate: Date | null } | null;
  disableFutureDates?: boolean; // If true, disables future dates (for reports). If false/undefined, disables past dates (for scheduling)
}

// Week starts from Monday (normal calendar format)
const daysShort = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label,
  name,
  error,
  onChange,
  disableFutureDates = false,
  value,
}) => {
  // Days are compared with isEqual against local-midnight calendar cells, so
  // anything coming in from outside is normalised to the start of its day.
  const initialStart = value?.startDate ? startOfDay(value.startDate) : null;
  const initialEnd = value?.endDate ? startOfDay(value.endDate) : null;
  const [currentMonth, setCurrentMonth] = useState(initialStart ?? new Date());
  const [startDate, setStartDate] = useState<Date | null>(initialStart);
  const [endDate, setEndDate] = useState<Date | null>(initialEnd);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const valueStartTime = value?.startDate
    ? startOfDay(value.startDate).getTime()
    : null;
  const valueEndTime = value?.endDate ? startOfDay(value.endDate).getTime() : null;

  // Follow outside changes (a restored selection, a form reset). A click
  // already updated local state before onChange echoed it back, so those
  // compare equal and are skipped -- otherwise every click would snap the
  // calendar back to the start month.
  useEffect(() => {
    if (value === undefined) return;
    const localStart = startDate ? startDate.getTime() : null;
    const localEnd = endDate ? endDate.getTime() : null;
    if (localStart === valueStartTime && localEnd === valueEndTime) return;
    setStartDate(valueStartTime !== null ? new Date(valueStartTime) : null);
    setEndDate(valueEndTime !== null ? new Date(valueEndTime) : null);
    if (valueStartTime !== null) setCurrentMonth(new Date(valueStartTime));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value === undefined, valueStartTime, valueEndTime]);

  const start = startOfMonth(currentMonth);
  const end = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start, end });

  // Calculate offset for first day of month
  // getDay() returns 0 for Sunday, 1 for Monday, etc.
  // We want Monday = 0, Tuesday = 1, ..., Sunday = 6
  // Formula: (getDay() + 6) % 7 converts Sunday=0 to Sunday=6, Monday=1 to Monday=0, etc.
  const firstDayOfWeek = (getDay(start) + 6) % 7;
  
  // Create array with empty cells for alignment + days of month
  const calendarDays = [
    ...Array(firstDayOfWeek).fill(null), // Empty cells before first day
    ...daysInMonth, // Actual days of the month
  ];

  // Get today's date at start of day for comparison
  const today = startOfDay(new Date());

  // Check if a date is disabled
  // If disableFutureDates is true, disable dates after today (for reports)
  // If disableFutureDates is false, disable dates before today (for scheduling)
  const isDateDisabled = (date: Date) => {
    if (disableFutureDates) {
      return isAfter(startOfDay(date), today);
    } else {
      return isBefore(startOfDay(date), today);
    }
  };

  const handleDateClick = (date: Date) => {
    // Don't allow clicking on disabled dates
    if (isDateDisabled(date)) {
      return;
    }

    if (!startDate || (startDate && endDate)) {
      const newStartDate = date;
      setStartDate(newStartDate);
      setEndDate(null);
      onChange?.({ startDate: newStartDate, endDate: null });
    } else if (startDate && !endDate) {
      if (isBefore(date, startDate)) {
        const newStartDate = date;
        const newEndDate = startDate;
        setEndDate(newEndDate);
        setStartDate(newStartDate);
        onChange?.({ startDate: newStartDate, endDate: newEndDate });
      } else {
        const newEndDate = date;
        setEndDate(newEndDate);
        onChange?.({ startDate: startDate, endDate: newEndDate });
      }
    }
  };

  const isInRange = (date: Date) => {
    if (startDate && !endDate && hoverDate) {
      return (
        (isAfter(date, startDate) && isBefore(date, hoverDate)) ||
        isEqual(date, startDate) ||
        isEqual(date, hoverDate)
      );
    }
    if (startDate && endDate) {
      return (
        (isAfter(date, startDate) && isBefore(date, endDate)) ||
        isEqual(date, startDate) ||
        isEqual(date, endDate)
      );
    }
    return false;
  };

  return (
    <div className="space-y-1.5 animate-fade-in">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          className="px-2 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => setCurrentMonth(addDays(start, -1))}
          disabled={
            disableFutureDates
              ? false // For reports, allow going to past months
              : isBefore(
                  startOfMonth(addDays(start, -1)),
                  startOfMonth(today)
                ) // For scheduling, disable past months
          }
        >
          &lt;
        </button>
        <span className="font-semibold">
          {format(currentMonth, "MMMM yyyy")}
        </span>
        <button
          type="button"
          className="px-2 py-1 text-sm border rounded"
          onClick={() => setCurrentMonth(addDays(end, 1))}
        >
          &gt;
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-sm font-medium">
        {daysShort.map((day) => (
          <div key={day} className="text-gray-500">
            {day}
          </div>
        ))}

        {calendarDays.map((day, index) => {
          // Skip rendering for empty cells (null values)
          if (!day) {
            return <div key={`empty-${index}`} className="p-2" />;
          }

          const inRange = isInRange(day);
          const isStart = startDate && isEqual(day, startDate);
          const isEnd = endDate && isEqual(day, endDate);
          const isDisabled = isDateDisabled(day);

          return (
            <div
              key={day.toString()}
              onClick={() => handleDateClick(day)}
              onMouseEnter={() => !isDisabled && setHoverDate(day)}
              className={`p-2 rounded 
                ${
                  isDisabled
                    ? "text-gray-300 cursor-not-allowed opacity-50"
                    : isStart || isEnd
                    ? "bg-primary-default text-white cursor-pointer"
                    : inRange
                    ? "bg-primary-light text-white cursor-pointer"
                    : "hover:bg-gray-200 cursor-pointer"
                }
              `}
            >
              {format(day, "d")}
            </div>
          );
        })}
      </div>

      {error && typeof error === "object" && (() => {
        const msg =
          ("message" in error && error.message)
            ? String(error.message)
            : ("startDate" in error && (error as any).startDate?.message)
              ? String((error as any).startDate.message)
              : ("endDate" in error && (error as any).endDate?.message)
                ? String((error as any).endDate.message)
                : null;
        return msg ? (
          <p className="mt-1 text-sm text-danger-default animate-fade-in">
            {msg}
          </p>
        ) : null;
      })()}

      {/* Hidden inputs for react-hook-form */}
      <input
        type="hidden"
        name={`${name}.startDate`}
        value={startDate ? startDate.toISOString() : ""}
      />
      <input
        type="hidden"
        name={`${name}.endDate`}
        value={endDate ? endDate.toISOString() : ""}
      />
    </div>
  );
};

export default DateRangePicker;
